/**
 * OrganisationNameField — isolated draft + debounced save so the
 * live input value never gets clobbered by concurrent server
 * responses, and we don't fire a round trip + toast on every
 * keystroke. Fixes the iter72.9 branding-save regression where
 * deleting characters re-populated the field with "Elara".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const DEBOUNCE_MS = 700;
const MIN_LEN = 2;
const MAX_LEN = 80;

export function OrganisationNameField({ value, onSave }) {
  // Local draft — the source of truth while the user is typing.
  const [draft, setDraft] = useState(value || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  const timer = useRef(null);
  const latestSave = useRef(0); // monotonic request id — guards against
                                 // out-of-order responses overwriting a
                                 // newer local edit.

  // Seed the draft the FIRST time a non-empty server value arrives.
  // After that, server updates never clobber the user's live edits —
  // debounced save is the only writer.
  const seeded = useRef(false);
  useEffect(() => {
    if (!seeded.current && value) {
      setDraft(value);
      seeded.current = true;
    }
  }, [value]);

  // Cleanup pending timer on unmount.
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const persist = async (next) => {
    const trimmed = next.trim();
    if (trimmed.length < MIN_LEN) {
      setStatus('error');
      setError(`Name must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (trimmed.length > MAX_LEN) {
      setStatus('error');
      setError(`Name must be ${MAX_LEN} characters or fewer.`);
      return;
    }
    setError(null);
    setStatus('saving');
    const myId = ++latestSave.current;
    try {
      await onSave(trimmed);
      // Only flip to "saved" if no newer save has started since.
      if (myId === latestSave.current) {
        setStatus('saved');
        setTimeout(() => {
          // Fade out the saved tick after 1.5s — but only if the user
          // hasn't started typing again.
          if (myId === latestSave.current) setStatus('idle');
        }, 1500);
      }
    } catch (e) {
      if (myId === latestSave.current) {
        setStatus('error');
        setError('Save failed. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    setStatus('idle');
    setError(null);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), DEBOUNCE_MS);
  };

  const handleBlur = () => {
    // On blur, flush any pending debounced save immediately.
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (draft !== (value || '')) persist(draft);
  };

  const indicator = (() => {
    if (status === 'saving') return <Loader2 className="w-4 h-4 text-[#A1A1AA] animate-spin" aria-label="Saving" />;
    if (status === 'saved') return <Check className="w-4 h-4 text-[#1ABC9C]" aria-label="Saved" />;
    if (status === 'error') return <AlertCircle className="w-4 h-4 text-red-400" aria-label="Error" />;
    return null;
  })();

  return (
    <div className="w-56 flex flex-col items-end gap-1">
      <div className="relative w-full">
        <Input
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={MAX_LEN}
          className={cn(
            'bg-[#2B2B2B] border-white/10 pr-9',
            status === 'error' && 'border-red-500/50'
          )}
          data-testid="organisation-name-input"
          aria-label="Organisation name"
          placeholder="Organisation name"
        />
        {indicator && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2" data-testid={`org-name-status-${status}`}>
            {indicator}
          </span>
        )}
      </div>
      {error && (
        <span className="text-xs text-red-400" data-testid="org-name-error">{error}</span>
      )}
    </div>
  );
}

export default OrganisationNameField;
